import {
  FormControl,
  InputAdornment,
  Stack,
  List,
  SelectProps,
  CircularProgress,
  Box,
  FormHelperText
} from '@mui/material'
import { FC, memo, useMemo, useRef, useState, useLayoutEffect } from 'react'
import theme from 'theme'
import {
  StyledInputLabel,
  StyledListSubheader,
  StyledMenuItem,
  StyledSelect,
  StyledTextField
} from './selectWithPopoverMenuV2.styles'
import SearchIcon from '@mui/icons-material/Search'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import CheckboxV2 from '../checkbox-v2/CheckboxV2'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import { TreeSelectOption } from 'models'
import { DEFAULT_SELECT_MENU_PROPS } from './SelectWithPopoverMenuV2.configs'
import HoveredPopover from '../../hovered-popover'

export type SelectWithPopoverMenuV2Props = Omit<SelectProps, 'onChange' | 'renderValue'> & {
  label: string
  value?: TreeSelectOption[]
  error?: boolean
  labelId?: string
  helperText?: any
  selectId?: string
  isLoading?: boolean
  searchable?: boolean
  emptyMessage?: string
  options: TreeSelectOption[]
  selectAllCheckboxLabel?: string
  placeholder?: string
  onChange?: (selectedTree: TreeSelectOption[]) => void
  onClose?: () => void
  invalidItems?: Set<string>
  /* Overrides the default "N selected" summary when the caller needs the
   * selection spelled out. */
  renderSelected?: (selected: TreeSelectOption[]) => string
  /* Options that render greyed out and cannot be toggled — also excluded from
   * select-all — e.g. a value already spoken for by a sibling select. */
  disabledItems?: Set<string>
  /* An extra select-all-style checkbox under select-all that toggles a preset
   * subset, e.g. the default statuses. The menu stays open so the change shows. */
  listAction?: { label: string; checked: boolean; indeterminate?: boolean; onToggle: () => void }
}

const SelectWithPopoverMenuV2: FC<SelectWithPopoverMenuV2Props> = ({
  value = [],
  error,
  label,
  labelId,
  selectId,
  options = [],
  multiple = true,
  isLoading = false,
  searchable = false,
  helperText = 'Error',
  MenuProps = DEFAULT_SELECT_MENU_PROPS,
  emptyMessage = 'No options found',
  selectAllCheckboxLabel = 'All',
  placeholder,
  onChange,
  onClose: onCloseProp,
  invalidItems,
  renderSelected,
  disabledItems,
  listAction,
  ...props
}) => {
  const [searchText, setSearchText] = useState('')
  const [hoveredOption, setHoveredOption] = useState<string | null>(null)
  const selectRef = useRef<any>(null)

  const handleOnOpenSelect = () => {
    if (selectRef.current) {
      selectRef.current.classList.add('Mui-focused')
      selectRef.current.previousSibling?.classList.add('Mui-focused')
    }
  }

  const handleOnCloseSelect = () => {
    if (selectRef.current) {
      selectRef.current.classList.remove('Mui-focused')
      selectRef.current.previousSibling?.classList.remove('Mui-focused')
    }
    setTimeout(() => {
      setSearchText('')
    }, 0)
    onCloseProp?.()
  }

  const removeHoverState = () => {
    setHoveredOption(null)
  }

  // Helper: Flatten tree to get all selected values
  const flattenTree = (tree: TreeSelectOption[]): string[] => {
    const values: string[] = []
    tree.forEach((node) => {
      values.push(node.value)
      if (node.children) {
        node.children.forEach((child) => values.push(child.value))
      }
    })
    return values
  }

  // Helper: Rebuild tree from selected values
  const rebuildSelectedTree = (selectedValues: string[]): TreeSelectOption[] => {
    const selectedSet = new Set(selectedValues)
    const result: TreeSelectOption[] = []

    options.forEach((option) => {
      const isParentSelected = selectedSet.has(option.value)
      const selectedChildren = option.children?.filter((child) => selectedSet.has(child.value)) || []

      // Include parent if it's selected OR has selected children
      if (isParentSelected || selectedChildren.length) {
        result.push({
          ...option,
          label: option.label,
          value: option.value,
          ...(selectedChildren.length && { children: selectedChildren })
        })
      }
    })

    return result
  }

  // Custom render value function
  const renderValue = () => {
    if (renderSelected) return renderSelected(value)

    // Get parent values that have children (these shouldn't be counted as they're just grouping)
    const parentValuesWithChildren = options
      .filter((option) => option.children && option.children.length > 0)
      .map((option) => option.value)

    // Count: child options + parent options without children (standalone options)
    // Exclude: parent options that have children
    const selectedCount = selectedValues.filter((val) => !parentValuesWithChildren.includes(val)).length

    if (!selectedCount) {
      return placeholder || ''
    }

    // Check if all options are selected
    const totalCount = options.reduce((count, opt) => {
      if (opt.children && opt.children.length > 0) {
        return count + opt.children.length
      }
      return count + 1
    }, 0)

    if (selectedCount === totalCount) {
      return `${selectAllCheckboxLabel} (${options.length})`
    }

    return `${selectedCount} selected`
  }

  // Helper function to get all child values for a parent
  const getChildValues = (option: TreeSelectOption): string[] => {
    return option.children?.map((child) => child.value) || []
  }

  // Helper function to get all values (parents + children)
  const getAllValues = (): string[] => {
    const parentValues: string[] = []
    const childValues: string[] = []

    displayedOptions.forEach((option) => {
      parentValues.push(option.value)
      if (option.children) {
        const childrenOfThisParent = option.children.map((child) => child.value)
        childValues.push(...childrenOfThisParent)
      }
    })

    const allValues = [...parentValues, ...childValues]
    return disabledItems ? allValues.filter((value) => !disabledItems.has(value)) : allValues
  }

  // Check if parent is checked (all children are selected)
  const isParentChecked = (option: TreeSelectOption): boolean => {
    if (!option.children?.length) {
      return selectedValues.includes(option.value)
    }
    const childValues = getChildValues(option)
    const areAllChildrenSelected = childValues.every((childValue) => selectedValues.includes(childValue))
    return areAllChildrenSelected
  }

  // Check if parent is indeterminate (some but not all children selected)
  const isParentIndeterminate = (option: TreeSelectOption): boolean => {
    if (!option.children?.length) {
      return false
    }
    const childValues = getChildValues(option)
    const selectedCount = childValues.filter((childValue) => selectedValues.includes(childValue)).length
    const hasAtLeastOneSelected = !!selectedCount && selectedCount < childValues.length
    return hasAtLeastOneSelected
  }

  // Check if all items are selected
  const areAllOptionsSelected = (): boolean => {
    const allValues = getAllValues()
    const areAllChecked = !!allValues.length && allValues.every((val) => selectedValues.includes(val))
    return areAllChecked
  }

  // Check if some items are selected
  const hasSelectedOption = (): boolean => {
    const allValues = getAllValues()
    const selectedCount = allValues.filter((val) => selectedValues.includes(val)).length
    return selectedCount > 0 && selectedCount < allValues.length
  }

  // Handle select all toggle
  const handleSelectAll = (event: React.MouseEvent) => {
    event.stopPropagation()
    const areAllSelected = areAllOptionsSelected()
    const allValues = getAllValues()
    const newValues = areAllSelected ? [] : allValues
    const selectedTree = rebuildSelectedTree(newValues)
    onChange?.(selectedTree)
  }

  // Handle parent checkbox toggle
  const handleParentToggle = (option: TreeSelectOption) => (event: React.MouseEvent) => {
    event.stopPropagation()
    if (disabledItems?.has(option.value)) return
    const childValues = getChildValues(option)
    const allValues = [option.value, ...childValues]
    const isChecked = isParentChecked(option)

    let newValues: string[]
    if (isChecked) {
      // Unselect parent and all children
      newValues = selectedValues.filter((val) => !allValues.includes(val))
    } else {
      // Select parent and all children
      const combinedValues = [...selectedValues, ...allValues]
      newValues = Array.from(new Set(combinedValues))
    }

    const selectedTree = rebuildSelectedTree(newValues)
    onChange?.(selectedTree)
  }

  // Handle child checkbox toggle in popover
  const handleChildToggle = (parentOption: TreeSelectOption, childValue: string) => (event: React.MouseEvent) => {
    event.stopPropagation()

    const isChildSelected = selectedValues.includes(childValue)
    let newValues: string[]

    if (isChildSelected) {
      // Unselect child
      newValues = selectedValues.filter((val) => val !== childValue)
    } else {
      // Select child
      const childValues = getChildValues(parentOption)
      const valuesWithNewChild = [...selectedValues, childValue]
      const allChildrenSelected = childValues.every((val) => valuesWithNewChild.includes(val))

      // If all children are now selected, also add parent
      if (allChildrenSelected) {
        valuesWithNewChild.push(parentOption.value)
      }

      newValues = Array.from(new Set(valuesWithNewChild))
    }

    const selectedTree = rebuildSelectedTree(newValues)
    onChange?.(selectedTree)
  }

  // Handle "All" checkbox in popover
  const handlePopoverSelectAll = (option: TreeSelectOption) => (event: React.MouseEvent) => {
    event.stopPropagation()
    const childValues = getChildValues(option)
    const isChecked = isParentChecked(option)

    let newValues: string[]
    if (isChecked) {
      // Unselect all children and parent
      newValues = selectedValues.filter((val) => !childValues.includes(val) && val !== option.value)
    } else {
      // Select all children and parent
      const allValues = [option.value, ...childValues]
      const combinedValues = [...selectedValues, ...allValues]
      newValues = Array.from(new Set(combinedValues))
    }

    const selectedTree = rebuildSelectedTree(newValues)
    onChange?.(selectedTree)
  }

  const displayedOptions = useMemo(() => {
    return options.reduce((results, opt) => {
      const searchLower = searchText.toLowerCase()
      const parentMatches = opt.label.toLowerCase().includes(searchLower)
      const childMatches = opt.children?.some((child) => child.label.toLowerCase().includes(searchLower))
      if (parentMatches || childMatches) {
        const filteredChildren = opt.children?.filter((child) => child.label.toLowerCase().includes(searchLower))
        results.push({
          ...opt,
          children: filteredChildren
        })
      }
      return results
    }, [] as TreeSelectOption[])
  }, [searchText, options])

  // Internal: Flatten value prop to get selected values for checkbox state
  const selectedValues = useMemo(() => flattenTree(value), [value])

  useLayoutEffect(() => {
    // Remove focused state when loading
    handleOnCloseSelect()
  }, [isLoading])

  return (
    <>
      <FormControl fullWidth>
        <StyledInputLabel id={labelId}>{label}</StyledInputLabel>
        <StyledSelect
          ref={selectRef}
          id={selectId}
          label={label}
          value={selectedValues}
          labelId={labelId}
          multiple={multiple}
          MenuProps={MenuProps}
          /* An empty selection does not count as filled, so without this MUI
           * skips renderValue and the closed field shows nothing. An unshrunk
           * label sits in the slot the placeholder would fill, so the two are
           * mutually exclusive unless the caller opts in via displayEmpty. */
          displayEmpty={Boolean(placeholder) && !label}
          renderValue={renderValue}
          onOpen={handleOnOpenSelect}
          onClose={handleOnCloseSelect}
          IconComponent={KeyboardArrowDownIcon}
          disabled={isLoading || props.disabled}
          endAdornment={
            isLoading ? (
              <InputAdornment position="end" sx={{ marginRight: '32px' }}>
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null
          }
          {...props}
        >
          {/* Search box */}
          {searchable && (
            <StyledListSubheader>
              <StyledTextField
                autoFocus
                autoComplete="off"
                fullWidth
                size="small"
                placeholder="Search"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                onChange={(event) => setSearchText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.stopPropagation()
                  }
                }}
              />
            </StyledListSubheader>
          )}
          {/* Options */}
          {displayedOptions.length ? (
            <Box>
              <StyledMenuItem onClick={handleSelectAll} onMouseEnter={removeHoverState}>
                <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center">
                  <CheckboxV2
                    label={`${selectAllCheckboxLabel} (${displayedOptions.length})`}
                    checked={areAllOptionsSelected()}
                    indeterminate={hasSelectedOption()}
                    disableFocusRipple
                  />
                </Stack>
              </StyledMenuItem>
              {listAction && (
                <StyledMenuItem
                  onMouseEnter={removeHoverState}
                  onClick={(event) => {
                    event.stopPropagation()
                    listAction.onToggle()
                  }}
                >
                  <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center">
                    <CheckboxV2
                      label={listAction.label}
                      checked={listAction.checked}
                      indeterminate={listAction.indeterminate}
                      disableFocusRipple
                    />
                  </Stack>
                </StyledMenuItem>
              )}
              {displayedOptions.map((option, parentIdx) => (
                <HoveredPopover
                  key={`parent-${parentIdx}`}
                  closeDelay={200}
                  disabled={!option.children?.length}
                  onMouseLeavePopover={removeHoverState}
                  trigger={({ onMouseEnter, onMouseLeave }) => (
                    <StyledMenuItem
                      value={option.value}
                      disabled={disabledItems?.has(option.value)}
                      className={hoveredOption === option.label ? 'hovered' : ''}
                    >
                      <Stack
                        width="100%"
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        onMouseEnter={(event) => {
                          setHoveredOption(option.label)
                          onMouseEnter(event)
                        }}
                        onMouseLeave={onMouseLeave}
                        onClick={handleParentToggle(option)}
                      >
                        <CheckboxV2
                          label={option.label}
                          checked={isParentChecked(option)}
                          indeterminate={isParentIndeterminate(option)}
                          tabIndex={-1}
                          disableFocusRipple
                        />
                        <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
                          {invalidItems?.has(option.value) && (
                            <ExclamationCircleIcon width={16} height={16} color={theme.colorTokens.info.attention} />
                          )}
                          {option.children?.length ? <ArrowForwardIosIcon /> : null}
                        </Stack>
                      </Stack>
                    </StyledMenuItem>
                  )}
                >
                  <List sx={{ padding: 0 }}>
                    <StyledMenuItem key="all" sx={{ padding: '12px 16px' }} onClick={handlePopoverSelectAll(option)}>
                      <CheckboxV2
                        label={`All (${option.children?.length || 0})`}
                        checked={isParentChecked(option)}
                        indeterminate={isParentIndeterminate(option)}
                        tabIndex={-1}
                        disableFocusRipple
                      />
                    </StyledMenuItem>
                    {option.children?.map((child, subIdx: number) => (
                      <StyledMenuItem
                        key={`sub-${subIdx}`}
                        sx={{ padding: '12px 16px' }}
                        onClick={handleChildToggle(option, child.value)}
                      >
                        <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              overflow: 'hidden',
                              minWidth: 0,
                              flex: 1,
                              '& .MuiTypography-root': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }
                            }}
                          >
                            <CheckboxV2
                              label={child.label}
                              checked={selectedValues.includes(child.value)}
                              tabIndex={-1}
                              disableFocusRipple
                            />
                          </Box>
                          {invalidItems?.has(child.value) && (
                            <ExclamationCircleIcon
                              width={16}
                              height={16}
                              color={theme.colorTokens.info.attention}
                              style={{ flexShrink: 0 }}
                            />
                          )}
                        </Stack>
                      </StyledMenuItem>
                    ))}
                  </List>
                </HoveredPopover>
              ))}
            </Box>
          ) : (
            <StyledMenuItem className="not-found">{emptyMessage}</StyledMenuItem>
          )}
        </StyledSelect>
        {error && helperText && (
          <FormHelperText error={error} sx={{ marginLeft: 0, color: `${theme.colors.errorLight} !important` }}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    </>
  )
}

export default memo(SelectWithPopoverMenuV2)
